import React, { useState, useEffect } from 'react';
import { X, Camera, Upload, Trash2, Loader, MapPin, Globe, Instagram, Facebook, Info } from 'lucide-react';
import { uploadProfilePhoto } from '../services/storageService';
import { validateProfilePhoto } from '../utils/profileHelpers';

const ProfileEditor = ({ isOpen, onClose, profile, onSave }) => {
  const [formData, setFormData] = useState({
    alias: '',
    bio: '',
    location: '',
    website: '',
    instagram: '',
    facebook: ''
  });

  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [coverPhotoFile, setCoverPhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState('');
  const [coverPhotoPreview, setCoverPhotoPreview] = useState('');
  const [newPhotos, setNewPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState({});

  // Initialize form with existing profile data
  useEffect(() => {
    if (profile && isOpen) {
      console.log('ProfileEditor - Initializing with profile:', {
        profilePhoto: profile.profilePhoto,
        alias: profile.alias
      });
      
      setFormData({
        alias: profile.alias || '',
        bio: profile.bio || '',
        location: profile.location || '',
        website: profile.website || '',
        instagram: profile.instagram || '',
        facebook: profile.facebook || ''
      });
      
      // Use profilePhoto if available
      const photoToUse = profile.profilePhoto || '';
      console.log('ProfileEditor - Setting profile photo preview:', photoToUse);
      setProfilePhotoPreview(photoToUse);
      setCoverPhotoPreview(profile.coverPhoto || '');
      setExistingPhotos(profile.photos || []);
      
      // Clear any previous file selections when modal opens
      setProfilePhotoFile(null);
      setCoverPhotoFile(null);
      setErrors({});
      setWarnings({});
    }
  }, [profile, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = await validateProfilePhoto(file, 'profile');
    if (!validation.valid) {
      setErrors(prev => ({ ...prev, profilePhoto: validation.error }));
      setWarnings(prev => ({ ...prev, profilePhoto: '' }));
      return;
    }

    setProfilePhotoFile(file);
    setProfilePhotoPreview(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, profilePhoto: '' }));
    
    // Show warning if image is smaller than recommended but still valid
    if (validation.warning) {
      setWarnings(prev => ({ ...prev, profilePhoto: validation.warning }));
    } else {
      setWarnings(prev => ({ ...prev, profilePhoto: '' }));
    }
  };

  const handleCoverPhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = await validateProfilePhoto(file, 'cover');
    if (!validation.valid) {
      setErrors(prev => ({ ...prev, coverPhoto: validation.error }));
      setWarnings(prev => ({ ...prev, coverPhoto: '' }));
      return;
    }

    setCoverPhotoFile(file);
    setCoverPhotoPreview(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, coverPhoto: '' }));
    
    // Show warning if image is smaller than recommended but still valid
    if (validation.warning) {
      setWarnings(prev => ({ ...prev, coverPhoto: validation.warning }));
    } else {
      setWarnings(prev => ({ ...prev, coverPhoto: '' }));
    }
  };

  const handleGalleryPhotosChange = async (e) => {
    const files = Array.from(e.target.files);
    const totalPhotos = existingPhotos.length + newPhotos.length + files.length;

    if (totalPhotos > 10) {
      setErrors(prev => ({ ...prev, gallery: `Maximum 10 photos allowed. You can add ${10 - existingPhotos.length - newPhotos.length} more.` }));
      return;
    }

    // Validate each file
    const validFiles = [];
    const invalidFiles = [];
    for (const file of files) {
      const validation = await validateProfilePhoto(file, 'gallery');
      if (validation.valid) {
        validFiles.push(file);
      } else {
        invalidFiles.push({ name: file.name, error: validation.error });
      }
    }

    const previews = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setNewPhotos(prev => [...prev, ...previews]);
    
    // Show error for invalid files
    if (invalidFiles.length > 0) {
      const errorMsg = invalidFiles.length === 1 
        ? `${invalidFiles[0].name}: ${invalidFiles[0].error}`
        : `${invalidFiles.length} file(s) could not be added. Check that they meet the requirements.`;
      setErrors(prev => ({ ...prev, gallery: errorMsg }));
    } else {
      setErrors(prev => ({ ...prev, gallery: '' }));
    }
  };

  const removeNewPhoto = (index) => {
    setNewPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingPhoto = (photoUrl) => {
    setExistingPhotos(prev => prev.filter(p => p !== photoUrl));
  };

  const uploadFile = async (file, type = 'gallery') => {
    const result = await uploadProfilePhoto(profile.userId, file, type);
    if (!result.success) {
      throw new Error(result.error || 'Upload failed');
    }
    return result.url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const newErrors = {};
    if (!formData.alias.trim()) {
      newErrors.alias = 'Alias is required';
    }
    if (formData.bio.length > 500) {
      newErrors.bio = 'Bio must be less than 500 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setUploading(true);

    try {
      const updates = { ...formData };

      // Upload profile photo if changed
      if (profilePhotoFile) {
        updates.profilePhoto = await uploadFile(profilePhotoFile, 'profile');
      }

      if (coverPhotoFile) {
        updates.coverPhoto = await uploadFile(coverPhotoFile, 'cover');
      }

      const uploadedGalleryPhotos = [];
      for (const photoData of newPhotos) {
        const url = await uploadFile(photoData.file, 'gallery');
        uploadedGalleryPhotos.push(url);
      }

      // Combine existing and new gallery photos
      updates.photos = [...existingPhotos, ...uploadedGalleryPhotos];
      updates.totalPhotos = updates.photos.length;

      // Call onSave callback
      await onSave(updates);

      // Close modal
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      let errorMessage = 'Failed to update profile. Please try again.';
      
      // Provide more specific error messages
      if (error.code === 'storage/quota-exceeded' || error.message?.includes('quota')) {
        errorMessage = 'Storage quota exceeded. Please contact support — an admin needs to free storage space.';
      } else if (error.code === 'storage/unauthorized') {
        errorMessage = 'Upload failed: Permission denied. Please check your connection and try again.';
      } else if (error.code === 'storage/canceled') {
        errorMessage = 'Upload was canceled. Please try again.';
      } else if (error.code === 'storage/unknown') {
        errorMessage = 'Upload failed: Unknown error. Please check your internet connection.';
      } else if (error.message) {
        errorMessage = `Upload failed: ${error.message}`;
      }
      
      setErrors({ submit: errorMessage });
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  const totalPhotos = existingPhotos.length + newPhotos.length;
  const canAddMore = totalPhotos < 10;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
          {/* Header - Sticky */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
            <h2 className="text-2xl font-bold text-gray-900">Edit Profile</h2>
            <button
              onClick={onClose}
              disabled={uploading}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            >
              <X size={24} className="text-gray-600" />
            </button>
          </div>

          {/* Form - Scrollable */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(90vh-80px)] overflow-y-auto">
          {/* Image Requirements Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-2">
            <div className="flex gap-2">
              <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-2">Image Requirements:</p>
                <ul className="space-y-1 text-blue-800">
                  <li>• <strong>Profile Picture:</strong> Square image, minimum 400×400px (recommended 800×800px)</li>
                  <li>• <strong>Cover Photo:</strong> Wide image, minimum 1200×300px (recommended 1920×480px)</li>
                  <li>• <strong>Gallery Photos:</strong> Any size, minimum 200×200px</li>
                  <li>• <strong>File Size:</strong> Maximum 5MB per image</li>
                  <li>• <strong>Formats:</strong> JPG, PNG, GIF, or WebP</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Cover Photo */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Cover Photo (Header Image)
            </label>
            <div className="relative w-full h-40 bg-[var(--pf-accent)] rounded-xl overflow-hidden group">
              {coverPhotoPreview && (
                <img
                  src={coverPhotoPreview}
                  alt="Cover"
                  className="w-full h-full object-cover"
                />
              )}
              <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <div className="text-center text-white">
                  <Camera size={32} className="mx-auto mb-2" />
                  <p className="text-sm">Click to upload cover photo</p>
                  <p className="text-xs opacity-80">1200×300px minimum</p>
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleCoverPhotoChange}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
            {errors.coverPhoto && <p className="text-red-500 text-sm mt-1">{errors.coverPhoto}</p>}
            {warnings.coverPhoto && <p className="text-amber-600 text-sm mt-1 flex items-center gap-1"><Info size={14} /> {warnings.coverPhoto}</p>}
            <p className="text-xs text-gray-500 mt-1">
              Wide banner image for your profile header • JPG, PNG, GIF, or WebP • Max 5MB
            </p>
          </div>

          {/* Profile Photo */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Profile Photo
            </label>
            <div className="flex items-center gap-4">
              <div className="relative w-24 h-24 rounded-full overflow-hidden group border-2 border-gray-200">
                {profilePhotoPreview ? (
                  <img
                    src={profilePhotoPreview}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[var(--pf-accent)] flex items-center justify-center text-white text-3xl font-bold">
                    {formData.alias ? formData.alias.charAt(0).toUpperCase() : '?'}
                  </div>
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <Camera className="text-white" size={20} />
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleProfilePhotoChange}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Square image, minimum 400×400px
                </p>
                <p className="text-xs text-gray-500">
                  Best results with 800×800px or larger • JPG, PNG, GIF, or WebP • Max 5MB
                </p>
              </div>
            </div>
            {errors.profilePhoto && <p className="text-red-500 text-sm mt-1">{errors.profilePhoto}</p>}
            {warnings.profilePhoto && <p className="text-amber-600 text-sm mt-1 flex items-center gap-1"><Info size={14} /> {warnings.profilePhoto}</p>}
          </div>

          {/* Alias */}
          <div>
            <label htmlFor="alias" className="block text-sm font-semibold text-gray-700 mb-2">
              Alias / Display Name *
            </label>
            <input
              type="text"
              id="alias"
              name="alias"
              value={formData.alias}
              onChange={handleInputChange}
              disabled={uploading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Your display name"
            />
            {errors.alias && <p className="text-red-500 text-sm mt-1">{errors.alias}</p>}
          </div>

          {/* Bio */}
          <div>
            <label htmlFor="bio" className="block text-sm font-semibold text-gray-700 mb-2">
              Bio
            </label>
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              disabled={uploading}
              rows={4}
              maxLength={500}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 resize-none"
              placeholder="Tell us about yourself..."
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.bio.length}/500 characters
            </p>
            {errors.bio && <p className="text-red-500 text-sm mt-1">{errors.bio}</p>}
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <MapPin size={16} />
              Location
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              disabled={uploading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="City, Country"
            />
          </div>

          {/* Social Links */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Social Links</h3>
            
            {/* Website */}
            <div>
              <label htmlFor="website" className="block text-xs text-gray-600 mb-1 flex items-center gap-2">
                <Globe size={14} />
                Website
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                disabled={uploading}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                placeholder="https://yourwebsite.com"
              />
            </div>

            {/* Instagram */}
            <div>
              <label htmlFor="instagram" className="block text-xs text-gray-600 mb-1 flex items-center gap-2">
                <Instagram size={14} />
                Instagram
              </label>
              <input
                type="text"
                id="instagram"
                name="instagram"
                value={formData.instagram}
                onChange={handleInputChange}
                disabled={uploading}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                placeholder="@username"
              />
            </div>

            {/* Facebook */}
            <div>
              <label htmlFor="facebook" className="block text-xs text-gray-600 mb-1 flex items-center gap-2">
                <Facebook size={14} />
                Facebook
              </label>
              <input
                type="text"
                id="facebook"
                name="facebook"
                value={formData.facebook}
                onChange={handleInputChange}
                disabled={uploading}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                placeholder="facebook.com/username"
              />
            </div>
          </div>

          {/* Gallery Photos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700">
                Photo Gallery ({totalPhotos}/10)
              </label>
              {canAddMore && (
                <label className="cursor-pointer bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2">
                  <Upload size={16} />
                  Add Photos
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    multiple
                    onChange={handleGalleryPhotosChange}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>

            {/* Photo Grid */}
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {/* Existing Photos */}
              {existingPhotos.map((photoUrl, index) => (
                <div key={`existing-${index}`} className="relative aspect-square rounded-lg overflow-hidden group">
                  <img
                    src={photoUrl}
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeExistingPhoto(photoUrl)}
                    disabled={uploading}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}

              {/* New Photos */}
              {newPhotos.map((photoData, index) => (
                <div key={`new-${index}`} className="relative aspect-square rounded-lg overflow-hidden group">
                  <img
                    src={photoData.preview}
                    alt={`New ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeNewPhoto(index)}
                    disabled={uploading}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                  </button>
                  <div className="absolute bottom-1 left-1 bg-blue-500 text-white text-xs px-2 py-0.5 rounded">
                    New
                  </div>
                </div>
              ))}
            </div>

            {errors.gallery && <p className="text-red-500 text-sm mt-2">{errors.gallery}</p>}
            {!canAddMore && (
              <p className="text-amber-600 text-sm mt-2">
                Maximum 10 photos reached. Remove some to add more.
              </p>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {errors.submit}
            </div>
          )}

          {/* Action Buttons - Sticky */}
          </form>
          
          <div className="flex gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={uploading}
              className="flex-1 px-6 py-3 pf-btn-primary rounded-lg font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditor;

